/**
 * Utility functions for the education strategy
 */

import { runAIMatches, runMultiMatches, fetchQueries, runMatchesAnalyze } from '../../api';
import centralDiscoveryQueue from '../../centralDiscoveryQueue';
import { DISCOVERY_TEMPLATE_TYPES } from '../../discoveryQueueSchema';

// Clean confirmation message for session restoration
export const restore_message = "I reviewed your existing workforce, and this is the distribution across different education backgrounds. Which education requirements would you like to target?";

/**
 * Generate education data and populate centralized queue
 * @param {Array} messages - Current conversation messages
 * @param {Function} setMessages - Function to update messages state
 * @param {Object} decision - Current decision object
 * @param {boolean} useCache - Whether to use cached data
 * @returns {Promise<Object>} The education data
 */
export const generateEducationData = async (messages, setMessages, decision, useCache = true) => {
  // Update thinking accordion
  if (setMessages) {
    setMessages(prev => {
      const updatedMessages = prev.map(msg =>
        msg.role === 'AI' && msg.content === 'Typing...'
          ? {
              ...msg,
              thinking: `${msg.thinking}\nGenerating education data and populating centralized queue...`
            }
          : msg
      );
      return updatedMessages;
    });
  }

  try {
    console.log('🎓 EDUCATION: Starting data generation and queue population');
    console.log('🎓 EDUCATION: *** CALL STACK DEBUG ***', new Error().stack);

    // Clear existing education queue items before adding new ones
    console.log('🎓 EDUCATION: Session ID check:', centralDiscoveryQueue.sessionId);
    if (centralDiscoveryQueue.sessionId) {
      const existingItems = centralDiscoveryQueue.getAllItems();
      console.log('🎓 EDUCATION: Total existing items:', existingItems.length);
      const educationItems = existingItems.filter(item => item.topic === 'education');
      console.log('🎓 EDUCATION: Found', educationItems.length, 'existing education items to clear:', educationItems.map(i => i.section_id));
      
      for (const item of educationItems) {
        console.log('🎓 EDUCATION: Attempting to remove:', item.queue_id, item.section_id);
        const removed = await centralDiscoveryQueue.removeQueueItem(item.queue_id);
        console.log('🎓 EDUCATION: Removal result for', item.section_id, ':', removed);
      }
      
      // Double-check after removal
      const remainingItems = centralDiscoveryQueue.getAllItems().filter(item => item.topic === 'education');
      console.log('🎓 EDUCATION: Remaining education items after clearing:', remainingItems.length);
    } else {
      console.log('🎓 EDUCATION: No session ID, skipping queue clearing');
    }

    // Fetch queries dictionary
    const queries = await fetchQueries();
    if (!queries || Object.keys(queries).length === 0) {
      throw new Error('Failed to fetch queries dictionary');
    }

    console.log('🎓 EDUCATION: Fetched queries:', Object.keys(queries).length);

    // Group queries by education categories
    const degreeQueries = [];
    const majorQueries = [];
    const processedQueryIds = new Set(); // Track processed queries to avoid duplicates

    Object.keys(queries).forEach((queryId) => {
      let queryData = queries[queryId];
      queryData.macro_taxonomy = (queryData.macro_taxonomy ? queryData.macro_taxonomy : []);
      
      if (queryData.macro_taxonomy.length === 0 || processedQueryIds.has(queryId)) {
        return;
      }
      
      // Check if any macro_taxonomy contains both "Education" and "Degree"
      const hasDegreeEducation = queryData.macro_taxonomy.some(tax => {
        const macroTax = tax.toLowerCase();
        return macroTax.indexOf("education") >= 0 && macroTax.indexOf("degree") >= 0;
      });
      
      // Check if any macro_taxonomy contains both "Education" and "Major"
      const hasMajorEducation = queryData.macro_taxonomy.some(tax => {
        const macroTax = tax.toLowerCase();
        return macroTax.indexOf("education") >= 0 && macroTax.indexOf("major") >= 0;
      });
      
      if (hasDegreeEducation) {
        degreeQueries.push({ id: queryId, name: queryData.name });
        processedQueryIds.add(queryId);
        console.log(`🎓 EDUCATION: Added to DEGREE: ${queryData.name} (${queryId}) - macro_taxonomy: ${queryData.macro_taxonomy[0]}`);
      } else if (hasMajorEducation) {
        majorQueries.push({ id: queryId, name: queryData.name });
        processedQueryIds.add(queryId);
        console.log(`🎓 EDUCATION: Added to MAJOR: ${queryData.name} (${queryId}) - macro_taxonomy: ${queryData.macro_taxonomy[0]}`);
      }
    });

    console.log('🎓 EDUCATION: Grouped queries:', {
      degrees: degreeQueries.length,
      majors: majorQueries.length
    });

    // Get university data from runMatchesAnalyze
    let universityData = [];
    try {
      // Get completed items for matches analyze
      const allQueueItems = centralDiscoveryQueue.getAllItems();
      const completedItems = allQueueItems.filter(item => item.disco_done === true);
      
      let icpRequirements = [];
      if (completedItems.length > 0) {
        icpRequirements = completedItems.map(item => ({
          section_type: item.section_type || 'Must Have',
          section_name: item.section_name,
          section_id: item.section_id,
          bool_str: item.bool_str || ''
        }));
      } else if (decision.getAllRequirements) {
        const existingReqs = decision.getAllRequirements();
        if (existingReqs && existingReqs.length > 0) {
          icpRequirements = existingReqs;
        }
      }
      
      if (icpRequirements.length > 0) {
        console.log('🎓 EDUCATION: Running matches analyze for university data...');
        const matchesAnalyzeResult = await runMatchesAnalyze(icpRequirements, {
          run_on_self_org: true
        });
        
        if (matchesAnalyzeResult?.analysis_info?.university_map && matchesAnalyzeResult?.analysis_info?.universities) {
          const universityMap = matchesAnalyzeResult.analysis_info.university_map;
          const universities = matchesAnalyzeResult.analysis_info.universities;
          
          // Create university entries with profile counts
          const universityEntries = Object.entries(universityMap).map(([universityId, profileIds]) => {
            const profileCount = Array.isArray(profileIds) ? profileIds.length : 0;
            const universityName = universities[universityId]?.name || `University ${universityId}`;
            return { universityId, name: universityName, profileCount };
          });
          
          // Sort by profile count and take top 10
          universityData = universityEntries
            .sort((a, b) => b.profileCount - a.profileCount)
            .slice(0, 6)
            .map(u => u.name);
            
          console.log('🎓 EDUCATION: Found university data:', universityData.length, 'universities');
        }
      }
    } catch (analyzeError) {
      console.error('🎓 EDUCATION: Error getting university data:', analyzeError);
      // Continue without university data
    }

    // Create queue items for each category
    const queueItems = [];

    // 1. Degree queue item
    if (degreeQueries.length > 0) {
      const degreeBoolStr = `((${degreeQueries.map(q => q.id).join(' OR ')}))`;
      const degreeQueueItem = {
        queue_id: `education_edu_${centralDiscoveryQueue.sessionId}`,
        section_id: 'edu',
        section_name: 'Degree',
        section_type: 'Must Have',
        section_order: 1,
        topic: 'education',
        bool_str: degreeBoolStr,
        disco_done: false,
        template_type: DISCOVERY_TEMPLATE_TYPES.DISTRIBUTION_BAR_CHART,
        ai_intro_message: 'I reviewed your existing workforce, and this is the distribution across different education degrees. Which degree level(s) would you like to target?',
        ux_config: {
          title: '',
          chart_title: 'Education Degrees',
          chart_subtitle: 'Distribution of degree levels in your workforce',
          confirm_button_text: 'Select at Least One Option',
          allow_empty_selection: true,
          hide_zero_percentage: true, // Only show items with non-zero percentages
          allowEmptySelection: true // Allow skipping this discovery by not selecting anything
        },
        template_props: {
          data: degreeQueries.map(q => ({
            name: q.name,
            percentage: 0, // Will be calculated dynamically
            queryId: q.id
          })),
          selectedItems: []
        }
      };
      queueItems.push(degreeQueueItem);
    }

    // 2. Major queue item
    if (majorQueries.length > 0) {
      const majorBoolStr = `((${majorQueries.map(q => q.id).join(' OR ')}))`;
      const majorQueueItem = {
        queue_id: `education_major_${centralDiscoveryQueue.sessionId}`,
        section_id: 'major',
        section_name: 'Major',
        section_type: 'Must Have',
        section_order: 2,
        topic: 'education',
        bool_str: majorBoolStr,
        disco_done: false,
        template_type: DISCOVERY_TEMPLATE_TYPES.DISTRIBUTION_BAR_CHART,
        ai_intro_message: 'I reviewed your existing workforce, and this is the distribution across different education majors. Which major(s) would you like to target?',
        ux_config: {
          title: '',
          chart_title: 'Education Majors',
          chart_subtitle: 'Distribution of education majors in your workforce',
          confirm_button_text: 'Select at Least One Option',
          allow_empty_selection: true,
          hide_zero_percentage: true, // Only show items with non-zero percentages
          allowEmptySelection: true // Allow skipping this discovery by not selecting anything
        },
        template_props: {
          data: majorQueries.map(q => ({
            name: q.name,
            percentage: 0, // Will be calculated dynamically
            queryId: q.id
          })),
          selectedItems: []
        }
      };
      queueItems.push(majorQueueItem);
    }

    // 3. Combined Schools queue item (Universities + School Lists)
    let schoolsQueueItem = null;
    let topSchoolLists = [];

    // First, try to get school lists from queries with "Education" and "Schools" taxonomy
    try {
      console.log('🎓 EDUCATION: Checking for school lists (Education & Schools)...');
      
      // Find queries with macro_taxonomy containing both "Education" and "Schools"
      const schoolListQueries = [];
      Object.keys(queries).forEach((queryId) => {
        let queryData = queries[queryId];
        queryData.macro_taxonomy = (queryData.macro_taxonomy ? queryData.macro_taxonomy : []);
        
        if (queryData.macro_taxonomy.length === 0) {
          return;
        }
        
        // Check if macro_taxonomy contains both "Education" and "Schools"
        const hasSchoolList = queryData.macro_taxonomy.some(tax => 
          tax.includes('Education') && tax.includes('School')
        );

        if (hasSchoolList) {
          schoolListQueries.push({ id: queryId, name: queryData.name });
          console.log(`🎓 EDUCATION: Found school list: ${queryData.name} (${queryId}) - macro_taxonomy: ${queryData.macro_taxonomy[0]}`);
        }
      });

      if (schoolListQueries.length > 0) {
        console.log('🎓 EDUCATION: Found', schoolListQueries.length, 'school list queries, running multimatches...');
        
        // Get completed items for multimatches
        const allQueueItems = centralDiscoveryQueue.getAllItems();
        const completedItems = allQueueItems.filter(item => item.disco_done === true);
        
        let icpRequirements = [];
        if (completedItems.length > 0) {
          icpRequirements = completedItems.map(item => ({
            section_type: item.section_type || 'Must Have',
            section_name: item.section_name,
            section_id: item.section_id,
            bool_str: item.bool_str || ''
          }));
        } else if (decision.getAllRequirements) {
          const existingReqs = decision.getAllRequirements();
          if (existingReqs && existingReqs.length > 0) {
            icpRequirements = existingReqs;
          }
        }

        if (icpRequirements.length > 0) {
          // Run multimatches to get counts for school list queries
          const schoolListIds = schoolListQueries.map(q => q.id);
          
          console.log('🎓 EDUCATION: Running multimatches for school lists with:', {
            requirements: icpRequirements.length,
            schoolListIds: schoolListIds.length
          });
          
          const multiMatchResults = await runMultiMatches(
            icpRequirements,
            schoolListIds,
            [], // to_match_requirements (empty)
            { run_on_self_org: true }
          );
          
          if (multiMatchResults) {
            const countByQueries = multiMatchResults.count_by_queries || multiMatchResults;
            
            // Sort by count and take top 5
            topSchoolLists = Object.entries(countByQueries)
              .map(([queryId, count]) => ({ queryId, count, name: queries[queryId]?.name || queryId }))
              .filter(result => result.count > 0)
              .sort((a, b) => b.count - a.count)
              .slice(0, 5);
            
            console.log('🎓 EDUCATION: School lists multimatches results:', {
              totalResults: Object.keys(countByQueries).length,
              topSchoolLists: topSchoolLists.map(s => ({ name: s.name, count: s.count }))
            });
          }
        }
      }
    } catch (schoolListError) {
      console.error('🎓 EDUCATION: Error processing school lists:', schoolListError);
      // Continue without school lists
    }

    // Create combined schools queue item if we have either university data or school lists
    if (universityData.length > 0 || topSchoolLists.length > 0) {
      console.log('🎓 EDUCATION: Creating combined schools queue item with:', {
        universities: universityData.length,
        schoolLists: topSchoolLists.length
      });

      // Create combined boolean string: university names OR top school list macro IDs
      let combinedBoolStr = '';
      
      // Add university names from runMatchesAnalyze
      const universityBoolStr = universityData.length > 0 ? 
        universityData.map(university => `education.school_name:"${university}"`).join(' OR ') : '';
      
      // Add top school list macro IDs from runMultiMatches
      const schoolListBoolStr = topSchoolLists.length > 0 ?
        topSchoolLists.map(s => s.queryId).join(' OR ') : '';
      
      // Combine both parts
      if (universityBoolStr && schoolListBoolStr) {
        combinedBoolStr = `(${universityBoolStr} OR ${schoolListBoolStr})`;
      } else if (universityBoolStr) {
        combinedBoolStr = `(${universityBoolStr})`;
      } else if (schoolListBoolStr) {
        combinedBoolStr = `(${schoolListBoolStr})`;
      }

      // Create combined context for UI display
      const combinedContext = {
        ...universityData.reduce((acc, university, index) => {
          acc[`university_${index}`] = university;
          return acc;
        }, {}),
        ...topSchoolLists.reduce((acc, schoolList, index) => {
          acc[`school_list_${index}`] = schoolList.name;
          return acc;
        }, {})
      };

      schoolsQueueItem = {
        queue_id: `education_schools_${centralDiscoveryQueue.sessionId}`,
        section_id: 'schools',
        section_name: 'Schools',
        section_type: 'Nice to Have',
        section_order: 3,
        topic: 'education',
        bool_str: combinedBoolStr,
        disco_done: false,
        template_type: DISCOVERY_TEMPLATE_TYPES.MULTI_ROW_SELECTOR,
        ai_intro_message: `Based on your workforce, here are universities and school types where great candidates studied 🎓\nSelect any schools or school categories you'd like to target! 🎯`,
        ux_config: {
          title: '',
          confirm_button_text: 'Confirm Schools',
          allow_empty_selection: true,
          ro_footer: false // Enable footer so users can type alternative school requirements
        },
        req_cxt: {
          school_cxt: combinedContext
        }
      };
      queueItems.push(schoolsQueueItem);
    }

    // 5. Academic Attributes queue item (Findem Magic & Academic) - only if matches found
    try {
      console.log('🎓 EDUCATION: Checking for academic attributes (Findem Magic & Academic)...');
      
      // Find queries with macro_taxonomy containing both "Findem Magic" and "Academic"
      const academicAttributeQueries = [];
      Object.keys(queries).forEach((queryId) => {
        let queryData = queries[queryId];
        queryData.macro_taxonomy = (queryData.macro_taxonomy ? queryData.macro_taxonomy : []);
        
        if (queryData.macro_taxonomy.length === 0) {
          return;
        }
        
        // Check if any macro_taxonomy contains both "Findem Magic" and "Academic"
        const hasAcademicAttribute = queryData.macro_taxonomy.some(tax => 
          tax.includes('Findem Magic') && tax.includes('Academic')
        );
        
        if (hasAcademicAttribute) {
          academicAttributeQueries.push({ id: queryId, name: queryData.name });
          console.log(`🎓 EDUCATION: Found academic attribute: ${queryData.name} (${queryId}) - macro_taxonomy: ${queryData.macro_taxonomy[0]}`);
        }
      });

      if (academicAttributeQueries.length > 0) {
        console.log('🎓 EDUCATION: Found', academicAttributeQueries.length, 'academic attributes, checking for matches...');
        
        // Get completed items for multimatches
        const allQueueItems = centralDiscoveryQueue.getAllItems();
        const completedItems = allQueueItems.filter(item => item.disco_done === true);
        
        let icpRequirements = [];
        if (completedItems.length > 0) {
          icpRequirements = completedItems.map(item => ({
            section_type: item.section_type || 'Must Have',
            section_name: item.section_name,
            section_id: item.section_id,
            bool_str: item.bool_str || ''
          }));
        } else if (decision.getAllRequirements) {
          const existingReqs = decision.getAllRequirements();
          if (existingReqs && existingReqs.length > 0) {
            icpRequirements = existingReqs;
          }
        }

        if (icpRequirements.length > 0) {
          // Run multimatches to check for non-zero results
          const toMatch = academicAttributeQueries.map(q => q.id);
          
          console.log('🎓 EDUCATION: Running multimatches for academic attributes with:', {
            requirements: icpRequirements.length,
            toMatch: toMatch.length
          });

          const multiMatchResults = await runMultiMatches(
            icpRequirements,
            toMatch,
            [], // to_match_requirements (empty)
            { run_on_self_org: true }
          );
          
          if (multiMatchResults) {
            const countByQueries = multiMatchResults.count_by_queries || multiMatchResults;
            const totalCount = Object.values(countByQueries).reduce((sum, count) => sum + (count || 0), 0);
            
            console.log('🎓 EDUCATION: Academic attributes multimatches results:', {
              totalCount,
              countByQueries
            });
            
            // Only add queue item if there are non-zero matches
            if (totalCount > 0) {
              // Filter out queries with zero matches
              const matchingQueries = academicAttributeQueries.filter(q => {
                const count = countByQueries[q.id] || 0;
                return count > 0;
              });
              
              if (matchingQueries.length > 0) {
                console.log('🎓 EDUCATION: Adding academic attributes queue item with', matchingQueries.length, 'matching attributes');
                
                const academicBoolStr = `((${matchingQueries.map(q => q.id).join(' OR ')}))`;
                const academicContext = matchingQueries.reduce((acc, query, index) => {
                  acc[`academic_${index}`] = query.name;
                  return acc;
                }, {});

                const academicQueueItem = {
                  queue_id: `education_fmmagic_${centralDiscoveryQueue.sessionId}`,
                  section_id: 'fmmagic',
                  section_name: 'Academic Attributes',
                  section_type: 'Nice to Have',
                  section_order: 4,
                  topic: 'education',
                  bool_str: academicBoolStr,
                  disco_done: false,
                  template_type: DISCOVERY_TEMPLATE_TYPES.MULTI_ROW_SELECTOR,
                  ai_intro_message: `Based on your workforce, here are academic attributes that could be valuable 🎓\nSelect any academic attributes you'd like to target! 🎯`,
                  ux_config: {
                    title: '',
                    confirm_button_text: 'Confirm Academic Attributes',
                    allow_empty_selection: true,
                    ro_footer: false // Enable footer so users can type alternative academic requirements
                  },
                  req_cxt: {
                    academic_cxt: academicContext
                  }
                };
                queueItems.push(academicQueueItem);
              } else {
                console.log('🎓 EDUCATION: No academic attributes with non-zero matches, skipping queue item');
              }
            } else {
              console.log('🎓 EDUCATION: No matches found for academic attributes, skipping queue item');
            }
          } else {
            console.log('🎓 EDUCATION: No multimatches results for academic attributes, skipping queue item');
          }
        } else {
          console.log('🎓 EDUCATION: No ICP requirements available for academic attributes check, skipping queue item');
        }
      } else {
        console.log('🎓 EDUCATION: No academic attributes found with Findem Magic & Academic taxonomy');
      }
    } catch (academicError) {
      console.error('🎓 EDUCATION: Error processing academic attributes:', academicError);
      // Continue without academic attributes - don't fail the entire strategy
    }

    // Add all queue items to centralized queue
    console.log('🎓 EDUCATION: Adding', queueItems.length, 'items to centralized queue');
    
    // Check if central discovery queue is properly initialized
    if (!centralDiscoveryQueue.sessionId) {
      console.error('❌ EDUCATION: Central discovery queue not initialized - no session ID');
      throw new Error('Central discovery queue not initialized - no session ID');
    }
    
    console.log('✅ EDUCATION: Central discovery queue is initialized with session ID:', centralDiscoveryQueue.sessionId);
    
    for (const queueItem of queueItems) {
      try {
        const addedItem = await centralDiscoveryQueue.addQueueItem(queueItem);
        
        // Calculate items count based on template type
        let itemsCount = 0;
        if (queueItem.template_type === DISCOVERY_TEMPLATE_TYPES.DISTRIBUTION_BAR_CHART) {
          itemsCount = queueItem.template_props.data ? queueItem.template_props.data.length : 0;
        } else if (queueItem.template_type === DISCOVERY_TEMPLATE_TYPES.MULTI_ROW_SELECTOR) {
          itemsCount = universityData.length;
        }
        
        console.log('✅ EDUCATION: Added queue item:', {
          section_id: addedItem.section_id,
          section_name: addedItem.section_name,
          template_type: queueItem.template_type,
          items_count: itemsCount
        });
      } catch (error) {
        console.error('❌ EDUCATION: Error adding queue item:', queueItem.section_id, error);
      }
    }

    // Force immediate save to backend database
    try {
      await centralDiscoveryQueue.saveToDatabase();
      console.log('✅ EDUCATION: Successfully synchronized', queueItems.length, 'queue items to backend database');
    } catch (saveError) {
      console.error('❌ EDUCATION: Error saving queue items to backend:', saveError);
    }

    // Update thinking accordion
    if (setMessages) {
      setMessages(prev => {
        const updatedMessages = prev.map(msg =>
          msg.role === 'AI' && msg.content === 'Typing...'
            ? {
                ...msg,
                thinking: `${msg.thinking}\nSuccessfully populated ${queueItems.length} education queue items.`
              }
            : msg
        );
        return updatedMessages;
      });
    }

    console.log('✅ EDUCATION: Successfully populated centralized queue with', queueItems.length, 'items');

    // Return success data for backward compatibility
    return {
      educationData: {
        totalItems: queueItems.length,
        degrees: degreeQueries.length,
        majors: majorQueries.length,
        universities: universityData.length
      },
      totalCount: degreeQueries.length + majorQueries.length + (universityData.length > 0 ? 1 : 0),
      aiMatchesResult: {
        educationData: {
          queueItemsCreated: queueItems.length,
          categories: queueItems.map(item => item.section_id)
        },
        educationTotal: queueItems.length
      }
    };

  } catch (error) {
    console.error('❌ EDUCATION: Error generating education data:', error);
    
    // Update thinking accordion with error
    if (setMessages) {
      setMessages(prev => {
        const updatedMessages = prev.map(msg =>
          msg.role === 'AI' && msg.content === 'Typing...'
            ? {
                ...msg,
                thinking: `${msg.thinking}\nError generating education data: ${error.message}`
              }
            : msg
        );
        return updatedMessages;
      });
    }

    throw error;
  }
};